import { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../../../config/firebase';

export function useImageUpload() {
  const [isUploading, setIsUploading] = useState(false);

  /**
   * Upload image to Firebase Storage with optional element context for auto-resizing
   * 
   * @param {File} file - The image file to upload
   * @param {Object} elementContext - Optional context for resizing:
   *   - elementId: string
   *   - bookId: string
   *   - spreadId: string
   *   - currentSize: { width, height }
   *   - canonicalDiagonal: number (optional)
   *   - orientation: 'portrait' | 'landscape'
   * @returns {Promise<string>} The uploaded image URL
   */
  const handleFileUpload = async (file, elementContext) => {
    if (!file) return;

    try {
      setIsUploading(true);

      // Read file as base64 data URL
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      // Call Firebase callable function
      const uploadFn = httpsCallable(functions, 'uploadImageToStorage');
      
      // ✨ NEW: Include element context if provided
      const payload = {
        base64,
        fileName: file.name || 'upload.png',
        contentType: file.type || 'image/png'
      };

      if (elementContext) {
        payload.elementId = elementContext.elementId;
        payload.bookId = elementContext.bookId;
        payload.spreadId = elementContext.spreadId;
        payload.elementCurrentSize = elementContext.currentSize;
        payload.canonicalDiagonal = elementContext.canonicalDiagonal;
        payload.elementOrientation = elementContext.orientation;
      }

      const res = await uploadFn(payload);

      // Access result safely (avoid TS unknown destructuring issue)
      const url = res && res.data && res.data.url;
      return url;
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    } finally {
      setIsUploading(false);
    }
  };

  return {
    isUploading,
    handleFileUpload
  };
}